import { FC, useEffect, useState } from 'react';

import { Link, NavLink, useLocation } from 'react-router-dom';

import {
  ADMIN_NAV_ITEMS,
  ADMIN_ROUTES,
  TAdminNavItem
} from '@/shared/constants';

import { useAdminMenuCounters } from '@/entities/admin-menu-counters';

import { AdminSidebarCounterBadge } from './AdminSidebarCounterBadge';
import { getAdminIcon } from './icons';

const SIDEBAR_WIDTH = 260;
const COLLAPSED_STORAGE_KEY = 'admin.sidebar.collapsed';

type TAdminNavGroup = Extract<TAdminNavItem, { type: 'group' }>;
type TAdminNavLink = Extract<TAdminNavItem, { type: 'item' }>;

interface IProps {
  mobileOpen: boolean;
  onMobileClose: () => void;
}

/**
 * Проверяет активность пункта меню.
 * @param href Ссылка пункта
 * @param pathname Текущий путь
 */
const isActiveHref = (href: string, pathname: string) => {
  return pathname === href || pathname.startsWith(`${href}/`);
};

/**
 * Возвращает начальное collapsed-состояние sidebar.
 */
const getInitialCollapsed = () => {
  try {
    return localStorage.getItem(COLLAPSED_STORAGE_KEY) === '1';
  } catch {
    return false;
  }
};

/**
 * Sidebar админки.
 * @param root0 Пропсы
 * @param root0.mobileOpen Открыт ли mobile sidebar
 * @param root0.onMobileClose Закрытие mobile sidebar
 */
export const AdminSidebar: FC<IProps> = ({ mobileOpen, onMobileClose }) => {
  const { pathname } = useLocation();
  const { menuCounters } = useAdminMenuCounters();
  const [collapsed, setCollapsed] = useState(getInitialCollapsed);
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({});
  const LogoIcon = getAdminIcon('chevrons-left');

  useEffect(() => {
    try {
      localStorage.setItem(COLLAPSED_STORAGE_KEY, collapsed ? '1' : '0');
    } catch {
      // localStorage может быть недоступен в приватном режиме.
    }
  }, [collapsed]);

  /**
   * Переключает состояние вложенной группы.
   * @param key Ключ группы
   */
  const toggleGroup = (key: string) => {
    setOpenGroups(current => ({ ...current, [key]: !current[key] }));
  };

  /**
   * Рендерит группу с вложенными пунктами.
   * @param item Элемент NAV типа group
   */
  const renderGroup = (item: TAdminNavGroup) => {
    const Icon = getAdminIcon(item.icon);
    const ChevronIcon = getAdminIcon('chevron-down');
    const isGroupActive = item.children.some(child =>
      isActiveHref(child.href, pathname)
    );
    const isOpen = openGroups[item.key] ?? isGroupActive;

    return (
      <div key={item.key}>
        <button
          className={`sidebar__item${
            collapsed && isGroupActive ? ' active' : ''
          }`}
          type='button'
          onClick={() => toggleGroup(item.key)}
        >
          <Icon className='sidebar__item-icon' />
          <span className='sidebar__item-label'>{item.label}</span>
          <ChevronIcon
            className={`sidebar__item-chevron${isOpen ? ' open' : ''}`}
          />
        </button>
        {isOpen ? (
          <div className='sidebar__subgroup open' id={`sg-${item.key}`}>
            {item.children.map(child => {
              const active = isActiveHref(child.href, pathname);

              return (
                <NavLink
                  key={child.key}
                  className={`sidebar__sub-item${active ? ' active' : ''}`}
                  to={child.href}
                  onClick={onMobileClose}
                >
                  {child.label}
                </NavLink>
              );
            })}
          </div>
        ) : null}
      </div>
    );
  };

  /**
   * Рендерит обычный пункт меню с бейджами ненулевых счётчиков.
   * @param item Элемент NAV типа item
   */
  const renderNavItem = (item: TAdminNavLink) => {
    const Icon = getAdminIcon(item.icon);
    const active = isActiveHref(item.href, pathname);
    // Бейджи ненулевых счётчиков — в одной колонке справа: два бейджа рядом съедали
    // подпись пункта («Фильмы в про…»), стопкой они занимают ширину одного
    const badges = (item.counters ?? []).flatMap(counter => {
      const count = menuCounters?.[counter.key];

      return count
        ? [
            <AdminSidebarCounterBadge
              count={count}
              counter={counter}
              key={counter.key}
              onNavigate={onMobileClose}
            />
          ]
        : [];
    });

    return (
      <NavLink
        key={item.key}
        className={`sidebar__item${active ? ' active' : ''}`}
        to={item.href}
        onClick={onMobileClose}
      >
        <Icon className='sidebar__item-icon' />
        <span className='sidebar__item-label'>{item.label}</span>
        {badges.length ? (
          <span className='sidebar__item-badges'>{badges}</span>
        ) : null}
      </NavLink>
    );
  };

  /**
   * Рендерит элемент навигации.
   * @param item Элемент NAV из v2-прототипа
   */
  const renderItem = (item: TAdminNavItem) => {
    if (item.type === 'label') {
      return (
        <div key={item.label} className='sidebar__group-label'>
          {item.label}
        </div>
      );
    }

    if (item.type === 'group') return renderGroup(item);

    return renderNavItem(item);
  };

  return (
    <aside
      className={[
        'sidebar',
        collapsed ? 'collapsed' : '',
        mobileOpen ? 'mobile-open' : ''
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <div className='sidebar__logo'>
        <Link to={ADMIN_ROUTES.users.index} onClick={onMobileClose}>
          <img
            className='sidebar__logo-mark'
            src='/admin-v2/img/logo-mark.svg'
            alt='Актёр'
          />
        </Link>
        <span className='sidebar__logo-text'>ктёр</span>
        <span
          style={{
            alignSelf: 'center',
            border: '1px solid rgba(255,255,255,0.15)',
            borderRadius: 4,
            color: 'rgba(255,255,255,0.5)',
            fontFamily: 'Roboto, sans-serif',
            fontSize: 10,
            fontWeight: 600,
            letterSpacing: '0.04em',
            marginLeft: 6,
            padding: '2px 6px'
          }}
        >
          v2
        </span>
      </div>
      <nav className='sidebar__nav'>{ADMIN_NAV_ITEMS.map(renderItem)}</nav>
      <button
        className='sidebar__toggle'
        type='button'
        title='Свернуть/развернуть'
        aria-label='Свернуть сайдбар'
        onClick={() => setCollapsed(value => !value)}
      >
        <LogoIcon className='sidebar__item-icon' />
      </button>
    </aside>
  );
};

export { SIDEBAR_WIDTH as ADMIN_SIDEBAR_WIDTH };
